import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePartidosEntrenador } from '@/hooks/usePartidosEntrenador';
import { PartidoCard } from '@/components/ui/PartidoCard';
import { Card } from '@/components/ui/Card';
import { DetallePartido } from '@/components/modals/DetallePartido';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { PartidoFilters, Partido } from '@/types';
import { ListaInformes } from '@/components/ui/ListaInformes';
import { DetalleInforme } from '@/components/ui/DetalleInforme';
import { useInformes } from '@/hooks/useInformes';
import { InformeResponse } from '@/types';

/**
 * Página de partidos asignados al entrenador
 */
export const PartidosAsignados: React.FC = () => {
  const navigate = useNavigate();
  const dateInputRef = React.useRef<HTMLInputElement>(null);
  const [fechaSeleccionada, setFechaSeleccionada] = useState<string>('');
  const [mostrarFinDeSemana, setMostrarFinDeSemana] = useState(true);
  const [partidoSeleccionado, setPartidoSeleccionado] = useState<Partido | null>(null);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  
  // Estados para la funcionalidad de informes
  const [mostrarInformes, setMostrarInformes] = useState(false);
  const [partidoInformesId, setPartidoInformesId] = useState<number | null>(null);
  const [informeSeleccionado, setInformeSeleccionado] = useState<InformeResponse | null>(null);
  
  // Calcular filtros basados en el estado
  const filtros: PartidoFilters = mostrarFinDeSemana && !fechaSeleccionada 
    ? getFinDeSemanaActual() 
    : fechaSeleccionada 
    ? getFiltrosPorFecha(fechaSeleccionada)
    : {};
  
  const { partidos, loading, error, refetch, setFiltros } = usePartidosEntrenador(filtros);
  
  // Hook para gestionar informes (solo se activa si hay un partidoInformesId)
  const {
    informes,
    loading: loadingInformes,
    obtenerInformes,
    actualizarInforme,
    refrescarInformes
  } = useInformes();

  /**
   * Obtiene las fechas del fin de semana actual
   */
  /**
   * Obtiene las fechas del fin de semana actual
   */
  function getFinDeSemanaActual(): PartidoFilters {
    const hoy = new Date();
    const diaSemana = hoy.getDay(); // 0 = domingo, 1 = lunes, ..., 6 = sábado
    
    let sabado: Date;
    let domingo: Date;
    
    if (diaSemana === 0) {
      // Si es domingo, el fin de semana actual es: sábado anterior + domingo actual
      sabado = new Date(hoy);
      sabado.setDate(hoy.getDate() - 1); // Ayer (sábado)
      sabado.setHours(0, 0, 0, 0);
      
      domingo = new Date(hoy);
      domingo.setHours(23, 59, 59, 999);
    } else if (diaSemana === 6) {
      // Si es sábado, el fin de semana actual es: sábado actual + domingo siguiente
      sabado = new Date(hoy);
      sabado.setHours(0, 0, 0, 0);
      
      domingo = new Date(hoy);
      domingo.setDate(hoy.getDate() + 1); // Mañana (domingo)
      domingo.setHours(23, 59, 59, 999);
    } else {
      // Cualquier otro día: calcular el próximo fin de semana
      const diasHastaSabado = 6 - diaSemana;
      sabado = new Date(hoy);
      sabado.setDate(hoy.getDate() + diasHastaSabado);
      sabado.setHours(0, 0, 0, 0);
      
      domingo = new Date(sabado);
      domingo.setDate(sabado.getDate() + 1);
      domingo.setHours(23, 59, 59, 999);
    }
    
    // Extender el rango para cubrir cualquier desfase de zona horaria
    const inicioExtendido = new Date(sabado);
    inicioExtendido.setHours(inicioExtendido.getHours() - 12);
    
    const finExtendido = new Date(domingo);
    finExtendido.setHours(finExtendido.getHours() + 12);
    
    return {
      fechaInicio: inicioExtendido.toISOString(),
      fechaFin: finExtendido.toISOString()
    };
  }

  /**
   * Obtiene filtros para una fecha específica ajustando la zona horaria
   */
  function getFiltrosPorFecha(fecha: string): PartidoFilters {
    // Crear fecha en zona horaria local para evitar desfases UTC
    const fechaLocal = new Date(fecha + 'T00:00:00');
    
    const fechaInicio = new Date(fechaLocal);
    fechaInicio.setHours(0, 0, 0, 0);
    
    const fechaFin = new Date(fechaLocal);
    fechaFin.setHours(23, 59, 59, 999);
    
    return {
      fechaInicio: fechaInicio.toISOString(),
      fechaFin: fechaFin.toISOString()
    };
  }

  /**
   * Maneja el cambio de fecha
   */
  const handleFechaChange = (fecha: string) => {
    setFechaSeleccionada(fecha);
    setMostrarFinDeSemana(false);
    
    const nuevosFiltros = fecha ? getFiltrosPorFecha(fecha) : {};
    setFiltros(nuevosFiltros);
  };

  /**
   * Vuelve a mostrar los partidos del fin de semana
   */
  const mostrarFinDeSemanaActual = () => {
    setFechaSeleccionada('');
    setMostrarFinDeSemana(true);
    setFiltros(getFinDeSemanaActual());
  };

  /**
   * Maneja el clic en una tarjeta de partido
   */
  const handlePartidoClick = (partidoId: number) => {
    const partido = partidos.find(p => p.id === partidoId);
    if (partido) {
      setPartidoSeleccionado(partido);
      setMostrarDetalle(true);
    }
  };

  /**
   * Cierra el modal de detalles
   */
  const handleCerrarDetalle = () => {
    setMostrarDetalle(false);
    setPartidoSeleccionado(null);
  };

  /**
   * Maneja la acción de ver informes
   */
  const handleVerInformes = (partidoId: number) => {
    // Aplicar delay ANTES de cerrar el modal y mostrar informes
    setTimeout(() => {
      // Cerrar el modal de detalle
      setMostrarDetalle(false);
      setPartidoSeleccionado(null);
      
      // Mostrar informes y obtenerlos
      setPartidoInformesId(partidoId);
      setMostrarInformes(true);
      
      // Obtener los informes del partido
      obtenerInformes(partidoId);
    }, 200); // Delay para permitir que se vea la animación del botón
  };

  /**
   * Maneja la selección de un informe para ver detalles
   */
  const handleSeleccionarInforme = (informe: InformeResponse) => {
    setInformeSeleccionado(informe);
  };

  /**
   * Maneja volver a la lista de informes
   */
  const handleVolverAListaInformes = () => {
    setInformeSeleccionado(null);
  };


  /**
   * Maneja la actualización de un informe
   */
  const handleActualizarInforme = async (id: number, data: Partial<InformeResponse>): Promise<InformeResponse> => {
    try {
      const informeActualizado = await actualizarInforme(id, data);
      refrescarInformes();
      return informeActualizado;
    } catch (error) {
      console.error('Error al actualizar informe:', error);
      throw error;
    }
  };

  /**
   * Maneja la acción de crear informe
   */
  const handleCrearInforme = (partidoId: number) => {
    const partido = partidos.find(p => p.id === partidoId);
    if (partido) {
      // Aplicar delay ANTES de cerrar el modal y navegar
      setTimeout(() => {
        // Cerrar el modal de detalle
        setMostrarDetalle(false);
        setPartidoSeleccionado(null);
        
        // Navegar a CrearInforme pasando los datos del partido
        navigate('/crear-informe', {
          state: {
            partidoData: {
              id: partido.id,
              nombre: partido.nombre,
              categoria: partido.categoria,
              grupo: partido.grupo,
              fechaHora: partido.fechaHora,
              competicion: partido.competicion
            }
          }
        });
      }, 200); // Delay para permitir que se vea la animación del botón
    }
  };

  /**
   * Maneja la actualización de un partido
   */
  const handlePartidoActualizado = async () => {
    // Recargar la lista de partidos para obtener los datos actualizados
    await refetch();
  };

  if (loading) {
    return (
      <div className="flex-1 p-4 flex items-center justify-center">
        <div className="text-secondary-400">Cargando partidos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-4">
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-4">
          <p className="text-red-400">{error}</p>
          <Button 
            onClick={refetch} 
            className="mt-2"
            variant="secondary"
          >
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4">
      <div className="space-y-6">
        {/* Vista de informes */}
        {mostrarInformes && partidoInformesId && (
          <div className="mb-6">
            {informeSeleccionado ? (
              <DetalleInforme
                informe={informeSeleccionado}
                onVolver={handleVolverAListaInformes}
                onActualizar={handleActualizarInforme}
              />
            ) : (
              <ListaInformes
                informes={informes}
                loading={loadingInformes}
                onSeleccionarInforme={handleSeleccionarInforme}
              />
            )}
          </div>
        )}

        {/* Vista normal de partidos (solo mostrar si no estamos viendo informes) */}
        {!mostrarInformes && (
          <>
            {/* Header */}
            <Card variant="glass" className="p-6 mb-6">
              <h1 className="text-2xl font-bold text-slate-100 mb-4">
                Partidos Asignados
              </h1>
              
              {/* Controles de fecha */}
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Button
                    onClick={mostrarFinDeSemanaActual}
                    variant={mostrarFinDeSemana ? 'primary' : 'outline'}
                    className={`flex-1 ${!mostrarFinDeSemana ? 'border-white/20 text-slate-300 hover:bg-white/5 hover:text-white' : ''}`}
                  >
                    Este fin de semana
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-300">
                    Seleccionar fecha específica:
                  </label>
                  <Input
                    ref={dateInputRef}
                    type="date"
                    value={fechaSeleccionada}
                    onChange={(e) => handleFechaChange(e.target.value)}
                    onClick={() => {
                      try {
                        if (dateInputRef.current && 'showPicker' in dateInputRef.current) {
                          dateInputRef.current.showPicker();
                        }
                      } catch (e) {
                        console.error('Error opening date picker:', e);
                      }
                    }}
                    className="w-full bg-slate-900/40 border-white/10 text-slate-200 focus:border-emerald-500/50 cursor-pointer"
                  />
                </div>
              </div>
            </Card>

            {/* Lista de partidos */}
            <div className="space-y-4">
              {partidos.length === 0 ? (
                <Card variant="glass" className="p-6 text-center">
                  <p className="text-slate-400">
                    No tienes partidos asignados para {mostrarFinDeSemana ? 'este fin de semana' : 'la fecha seleccionada'}.
                  </p>
                </Card>
              ) : (
                partidos.map((partido) => (
                  <PartidoCard
                    key={partido.id}
                    partido={partido}
                    onClick={() => handlePartidoClick(partido.id)}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal de detalles del partido */}
      <DetallePartido
        isOpen={mostrarDetalle}
        onClose={handleCerrarDetalle}
        partido={partidoSeleccionado}
        onVerInformes={handleVerInformes}
        onCrearInforme={handleCrearInforme}
        onPartidoActualizado={handlePartidoActualizado}
      />
    </div>
  );
};

export default PartidosAsignados;